import { computeAgeYears, sCurveFactor, roundToNearest } from './gvMath';
import { MANUFACTURERS, DEPRECIATION, ADJUSTMENTS } from '../config/mainCanopyConfig';

const linesetDeductionEur = (linesetJumps, cfg) => {
  if (linesetJumps == null || linesetJumps <= 0) return 0;
  const prorated = Math.min(linesetJumps / cfg.reline_horizon_jumps, 1.0) * cfg.reline_cost_eur;
  return prorated * cfg.weight;
};

export const calculateMainCanopy = ({ manufacturer, model, domYear, jumps, linesetJumps, sizeSqft, customNewPriceEur }) => {
  const notes = [];
  const adjustments = {};
  let newPriceEur;
  let mfrCfg = null;
  let brandKey = null;
  let mdlKey = null;

  if (customNewPriceEur) {
    newPriceEur = customNewPriceEur;
    notes.push('Custom price used. Brand and version adjustments not applied.');
  } else {
    const mfrKey = manufacturer.toLowerCase().trim();
    mdlKey = model.toLowerCase().trim();
    mfrCfg = MANUFACTURERS[mfrKey];
    if (!mfrCfg) {
      throw new Error(`Unknown manufacturer: ${manufacturer}. Add it to mainCanopyConfig before calculating.`);
    }
    const modelCfg = mfrCfg.models[mdlKey];
    if (!modelCfg) {
      throw new Error(`Unknown model: ${model}. Add it to mainCanopyConfig before calculating.`);
    }
    newPriceEur = modelCfg.new_price_eur;
    brandKey = mfrCfg.brand_key;
  }

  // Initial used factor
  const initialUsedFactor = DEPRECIATION.initial_used_factor;
  const usedBaselineEur = newPriceEur * initialUsedFactor;

  // Age depreciation (S-curve)
  const ageCfg = DEPRECIATION.age;
  const ageYears = computeAgeYears(domYear);
  const ageFactor = sCurveFactor(
    ageYears,
    ageCfg.max_age_years,
    ageCfg.midpoint_years,
    ageCfg.power_early,
    ageCfg.power_late,
    ageCfg.steepness,
  );
  const ageValueEur = usedBaselineEur * ageFactor;
  adjustments.ageFactor = Math.round(ageFactor * 1000) / 1000;

  // Jump depreciation (S-curve)
  const jumpCfg = DEPRECIATION.jumps;
  const actualJumps = jumps != null ? jumps : 0;
  const jumpHorizon = customNewPriceEur ? 2500 : jumpCfg.horizon_jumps;
  const jumpFactor = sCurveFactor(
    actualJumps,
    jumpHorizon,
    jumpCfg.midpoint_jumps,
    jumpCfg.power_early,
    jumpCfg.power_late,
    jumpCfg.steepness,
  );
  adjustments.jumpFactor = Math.round(jumpFactor * 1000) / 1000;

  // Combine age + jumps (worse_dominant)
  const secondaryWeight = DEPRECIATION.combination.secondary_weight;
  let valueEur;

  if (jumpFactor < ageFactor) {
    valueEur = usedBaselineEur * jumpFactor;
    const agePenalty = (1 - ageFactor) * secondaryWeight;
    valueEur *= (1 - agePenalty);
  } else {
    valueEur = ageValueEur;
    const jumpPenalty = (1 - jumpFactor) * secondaryWeight;
    valueEur *= (1 - jumpPenalty);
  }

  const combinedFactor = newPriceEur > 0 ? valueEur / newPriceEur : 0;
  adjustments.combinedFactor = Math.round(combinedFactor * 1000) / 1000;

  // Lineset deduction (direct EUR subtraction)
  const linesetCfg = DEPRECIATION.lineset;
  const linesetDeduction = linesetDeductionEur(linesetJumps, linesetCfg);
  if (linesetDeduction > 0) {
    valueEur = Math.max(DEPRECIATION.floor_value_eur, valueEur - linesetDeduction);
    notes.push(
      `Lineset deduction: ${Math.round(linesetDeduction)} EUR ` +
      `(${linesetJumps} jumps on lineset, ` +
      `~${Math.round(linesetCfg.reline_cost_eur)} EUR reline cost, ` +
      `${Math.round(linesetCfg.weight * 100)}% weight).`
    );
    adjustments.linesetDeductionEur = Math.round(linesetDeduction);
  }

  // Version penalty
  let versionMult;
  if (customNewPriceEur) {
    versionMult = 1.0;
  } else {
    versionMult = ADJUSTMENTS.version_penalty[mdlKey] ?? ADJUSTMENTS.version_penalty._default;
  }
  if (versionMult !== 1.0) {
    adjustments.versionPenalty = versionMult;
    notes.push(`Version penalty: ${Math.round((1 - versionMult) * 100)}% - newer version exists.`);
  }

  // Brand attractiveness
  let brandMult;
  if (customNewPriceEur) {
    brandMult = 1.0;
  } else if (brandKey) {
    const brandAdj = ADJUSTMENTS.brand_attractiveness;
    const mfrAdj = brandAdj[brandKey] || {};
    brandMult = mfrAdj[mdlKey] ?? mfrAdj._default ?? brandAdj._default_manufacturer;
  } else {
    brandMult = ADJUSTMENTS.brand_attractiveness._unknown_manufacturer;
    notes.push('Unknown brand - 8% desirability penalty applied.');
  }
  if (brandMult !== 1.0) {
    adjustments.brandAttractiveness = brandMult;
  }

  // Size adjustment (tiered)
  let sizeMult = 1.0;
  if (sizeSqft != null) {
    for (const tier of ADJUSTMENTS.size.tiers) {
      if (tier.above != null && sizeSqft > tier.above) {
        sizeMult = tier.penalty;
        notes.push(`Canopy ${tier.label}. Size penalty ${Math.round((1 - tier.penalty) * 100)}%.`);
        break;
      }
      if (tier.below != null && sizeSqft < tier.below) {
        sizeMult = tier.penalty;
        notes.push(`Canopy ${tier.label}. Size penalty ${Math.round((1 - tier.penalty) * 100)}%.`);
        break;
      }
    }
  }
  if (sizeMult !== 1.0) {
    adjustments.size = sizeMult;
  }

  // Final value
  const totalAdjustment = versionMult * brandMult * sizeMult;
  valueEur = valueEur * totalAdjustment;

  // Round to nearest 10, enforce floor
  valueEur = roundToNearest(valueEur, 10);
  valueEur = Math.max(valueEur, DEPRECIATION.floor_value_eur);

  const retentionPct = newPriceEur > 0 ? Math.round((valueEur / newPriceEur) * 1000) / 10 : 0;

  return {
    valueEur,
    newPriceEur: Math.round(newPriceEur * 100) / 100,
    retentionPct,
    notes,
    adjustments,
  };
};
